import { registerGlobalEvent } from './global-event';

export function convertDateToUTC(date: Date): Date {
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
}

export function compareDates(dateA: Date, dateB: Date): boolean {
    return dateA.toDateString() === dateB.toDateString();
}

export function startOfDay(date: Date) {
    return new Date(new Date(date).setHours(0, 0, 0, 0));
}

export function endOfDay(date: Date) {
    return new Date(new Date(date).setHours(23, 59, 59, 999));
}

export function toIsoString(date: Date) {
    const tzo = -date.getTimezoneOffset();
    const dif = tzo >= 0 ? '+' : '-';
    const pad = (num: number) => (num < 10 ? '0' : '') + num;

    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds()) + dif + pad(Math.floor(Math.abs(tzo) / 60)) + ':' + pad(Math.abs(tzo) % 60);
}

export function getDayFromDate(date: Date) {
    return new Date(date).toLocaleString('en-us', { weekday: 'long' });
}

export type TimeRange = {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
};

export function parseTimeDifference(timeDiff: number, roundUpMinutes: number = 0): TimeRange {
    const days = Math.floor(timeDiff / (24 * 60 * 60 * 1000));
    const hours = Math.floor(timeDiff / (60 * 60 * 1000) - days * 24);
    let minutes = Math.floor(timeDiff / (60 * 1000) - (days * 24 * 60 + hours * 60));
    let seconds = Math.floor(timeDiff / 1000 - (days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60));
    let milliseconds = Math.floor(timeDiff - (days * 24 * 60 * 60 * 1000 + hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000));

    if (roundUpMinutes) {
        minutes = Math.round(minutes / roundUpMinutes) * roundUpMinutes;
        seconds = 0;
        milliseconds = 0;
    }
    return { days, hours, minutes, seconds, milliseconds };
}

export function recursiveDateConversion(object: any) {
    for (let index in object) {
        if (typeof object[index] === 'object') {
            object[index] = recursiveDateConversion(object[index]);
        } else if (typeof object[index] === 'string') {
            if (/(\d{4})-(\d{2})-(\d{2})T(\d{2})\:(\d{2})\:(\d{2})/.test(object[index])) {
                object[index] = new Date(object[index]);
            }
        }
    }
    return object;
}

export function convertTo12HourFormat(time24: string) {
    const timeArray = time24.split(':');
    let hours = parseInt(timeArray[0]);
    const minutes = parseInt(timeArray[1]);
    let period = hours < 12 ? 'AM' : 'PM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const time12 = hours.toString().padStart(2, '0') + ':' + minutes.toString().padStart(2, '0') + ' ' + period;
    return time12;
}

export function convertTimeToDate(time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    const currentDate = new Date();
    currentDate.setHours(hours, minutes, 0, 0);
    return currentDate;
}

export function printPrice(price: number): string {
    if (price % 1 !== 0) {
        if (price < 1) {
            return price.toFixed(2).split('.')[1] + 'c';
        }
        return '$' + price.toFixed(2);
    }
    return '$' + price;
}

export function numberToCurrency(value: number) {
    const formatter = Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    });
    return formatter.format(value);
}

export function currencyToNumber(lang: string, currency: string, money: string) {
    const separatorDecimal = new Intl.NumberFormat(lang, {
        style: 'decimal',
    })
        .format(11.11)
        .replace(/\d/g, '');

    const separatorThousands = new Intl.NumberFormat(lang, {
        style: 'decimal',
    })
        .format(1111)
        .replace(/\d/g, '');

    const symbolOnLeft = new Intl.NumberFormat(lang, {
        style: 'currency',
        currency,
    })
        .format(1)
        .replace(new RegExp(`\\d|[${separatorDecimal}${separatorThousands}]*`, 'g'), '');

    const stringNumber = money
        .replace(new RegExp(`[${separatorThousands}]`, 'g'), '')
        .replace(separatorDecimal, '.')
        .replace(new RegExp(`[${symbolOnLeft}]`, 'g'), '');

    return parseFloat(stringNumber);
}

export function setupAccessibleOutline() {
    document.addEventListener('mousedown', () => {
        document.body.classList.add('disable-outline');
    });
    document.addEventListener('keydown', () => {
        document.body.classList.remove('disable-outline');
    });
}

export function toCamelCase(str: string) {
    const s = str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g);
    if (s) {
        return s.map((x) => x.slice(0, 1).toUpperCase() + x.slice(1).toLowerCase()).join('');
    }
}

export function updateLazyLoad() {
    if ((<any>window).lazyLoadInstance === undefined) {
        registerGlobalEvent('lazy-load-loaded', () => {
            if ((<any>window).lazyLoadInstance !== undefined) {
                (<any>window).lazyLoadInstance.update();
            }
        });
    } else {
        (<any>window).lazyLoadInstance.update();
    }
}

export function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
}

declare global {
    interface String {
        includesAll(this: string, terms: string[]): boolean;
        includesOne(this: string, terms: string[]): boolean;
    }
}

String.prototype.includesAll = function (this: string, terms: string[]) {
    let matches = 0;

    for (let term of terms) {
        if (this.includes(term)) matches++;
    }
    return matches === terms.length;
};

String.prototype.includesOne = function (this: string, terms: string[]) {
    for (let term of terms) {
        if (this.includes(term)) return true;
    }
    return false;
};
